/**
 * 画廊网格间距（三种屏系统一）
 */
export const GALLERY_LAYOUT = {
  columnGap: 18,
  rowGap: 15,
};

const FLAT_NATIVE = { w: 300, h: 400 };
const CURVED_NATIVE = { w: 496, h: 672 };
const OLED_NATIVE = { w: 128, h: 64 };

function displaySize(nativeWidth, nativeHeight, displayWidth) {
  return {
    width: displayWidth,
    height: Math.round((displayWidth * nativeHeight) / nativeWidth),
  };
}

const flatImage = displaySize(FLAT_NATIVE.w, FLAT_NATIVE.h, 218);
const curvedImage = displaySize(CURVED_NATIVE.w, CURVED_NATIVE.h, 244);

export const GALLERY_PANEL = {
  flat: {
    nativeWidth: FLAT_NATIVE.w,
    nativeHeight: FLAT_NATIVE.h,
    width: flatImage.width,
    height: flatImage.height,
    orientation: 'portrait',
    sizeLabel: '300 × 400',
    displayMode: 'eink-native',
    cardPadding: 12,
    cardWidth: 240,
    cardHeight: 385,
  },
  oled: {
    nativeWidth: OLED_NATIVE.w,
    nativeHeight: OLED_NATIVE.h,
    width: 256,
    height: 128,
    orientation: 'landscape',
    sizeLabel: '128 × 64',
    displayMode: 'oled-native',
    cardPadding: 10,
    cardWidth: 278,
    cardHeight: 210,
    maxColumnsPerRow: null,
  },
  curved: {
    nativeWidth: CURVED_NATIVE.w,
    nativeHeight: CURVED_NATIVE.h,
    width: curvedImage.width,
    height: curvedImage.height,
    orientation: 'portrait',
    sizeLabel: '496 × 672',
    displayMode: 'curved-native',
    cardPadding: 10,
    cardWidth: 264,
    cardHeight: 418,
  },
};

export const CATEGORY_LABELS = {
  oled: 'OLED (NANO-X)',
  flat: '直面屏 (GEN5)',
  curved: '曲面屏 (STAX)',
};

export const VALID_CATEGORIES = ['oled', 'flat', 'curved'];

export function isNativeGalleryMode(displayMode) {
  return typeof displayMode === 'string' && displayMode.endsWith('-native');
}

/** 显示尺寸相对原图是否为整数倍缩放（锐利、无拉伸） */
export function isIntegerPixelScale({ nativeWidth: nw, nativeHeight: nh, width: dw, height: dh }) {
  if (!nw || !nh || !dw || !dh) return false;
  if (Math.abs(dw * nh - dh * nw) > 1) return false;
  if (dw <= nw && nw % dw === 0 && nh % dh === 0) return true;
  if (dw >= nw && dw % nw === 0 && dh % nh === 0) return true;
  return false;
}

/** 一行 n 张卡片占用宽度（含卡片右侧列距，与卡片之间的间距一致） */
export function galleryRowContentWidth(columnCount, cardWidth, columnGap) {
  if (columnCount <= 0) return 0;
  return columnCount * cardWidth + columnCount * columnGap;
}

/** 一行能完整放下多少列（含最右列距；放不下则减列 / 换行） */
export function countColumnsForWidth(containerWidth, cardWidth, columnGap, maxColumnsPerRow) {
  if (containerWidth <= 0 || cardWidth <= 0) return 1;

  const slot = cardWidth + columnGap;
  let cols = Math.floor(containerWidth / slot);
  if (maxColumnsPerRow) {
    cols = Math.min(cols, maxColumnsPerRow);
  }
  cols = Math.max(1, cols);

  while (cols > 1 && galleryRowContentWidth(cols, cardWidth, columnGap) > containerWidth + 0.5) {
    cols -= 1;
  }

  return cols;
}

/**
 * 固定卡片尺寸 + 按容器宽度计算列数（列/行间距统一）
 */
export function computeGalleryLayout(panel, containerWidth) {
  if (!panel) return null;

  const columnGap = GALLERY_LAYOUT.columnGap;
  const rowGap = GALLERY_LAYOUT.rowGap;
  const cardWidth = panel.cardWidth;
  const cardHeight = panel.cardHeight;
  const columnCount = countColumnsForWidth(
    containerWidth,
    cardWidth,
    columnGap,
    panel.maxColumnsPerRow
  );

  const integerScale = isIntegerPixelScale({
    nativeWidth: panel.nativeWidth,
    nativeHeight: panel.nativeHeight,
    width: panel.width,
    height: panel.height,
  });

  return {
    columnCount,
    cardWidth,
    cardHeight,
    columnGap,
    rowGap,
    integerScale,
    displayPanel: { ...panel, integerScale },
    cssVars: {
      '--panel-aspect': `${panel.nativeWidth} / ${panel.nativeHeight}`,
      '--gallery-card-width': `${cardWidth}px`,
      '--gallery-card-padding': `${panel.cardPadding ?? 10}px`,
      '--gallery-image-width': `${panel.width}px`,
      '--gallery-image-height': `${panel.height}px`,
      '--gallery-column-gap': `${columnGap}px`,
      '--gallery-row-gap': `${rowGap}px`,
    },
  };
}
